import cx from "classnames";
import React from "react";
import { CSSTransition } from "react-transition-group";
import { setStyle } from "scroll-padlock";

import CloseIcon from "./assets/close.svg?react";
import { not } from "@/lib/not";
import { useStableId } from "@/lib/useStableId";
import { useUnloadBlocker } from "@/lib/useUnloadBlocker";
import { useModalQuestion } from "@/ModalQuestion";

import styles from "./PartModal.module.scss";

if (globalThis.window) setStyle();

export interface PartModalRef {
  show: () => void;
  hide: () => void;
}

interface PartModalProps extends React.PropsWithChildren {
  showed?: boolean;
  noHeader?: boolean;
  noCloseButton?: boolean;
  fullCover?: boolean;
  title?: string | React.ReactElement;
  titleAlignLeft?: boolean;
  titleWidth100?: boolean;
  dontCloseOnClickOutside?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
  afterClose?: () => void;
  parentModalRef?: PartModalRef;
  dontCloseOnEscapePress?: boolean;
  confirmCloseQuestion?: string | boolean;
  width?: string | number;
}

const CSSTransitionClasses = {
  appear: styles.transitionAppear,
  appearActive: styles.transitionAppearActive,
  enter: styles.transitionEnter,
  enterActive: styles.transitionEnterActive,
  exit: styles.transitionExit,
  exitActive: styles.transitionExitActive,
};

const PartModal = React.forwardRef<PartModalRef, PartModalProps>((props, ref) => {
  const {
    children,
    showed: showedFromProps,
    noHeader,
    noCloseButton,
    fullCover,
    title,
    titleAlignLeft,
    titleWidth100,
    dontCloseOnClickOutside,
    onOpen,
    onClose,
    afterClose,
    dontCloseOnEscapePress,
    confirmCloseQuestion,
    width = process.env.NEXT_PUBLIC_MODAL_DEFAULT_WIDTH || 640,
    // parentModalRef, TODO Refactor to close modals in cascade on close parent
  } = props;
  const nodeRef0 = React.useRef<HTMLDivElement | null>(null);

  const [showedFromState, setShowedFromState] = React.useState(false);
  const setShowedState = setShowedFromState; // Special fix for sonarCube

  const showed = showedFromProps ?? showedFromState;

  const modalId = useStableId();

  const {
    items: openedModalsIds,
    addItem: addOpenedModalsIdToRegistry,
    removeItem: removeOpenedModalsIdFromRegistry,
  } = useModalArray();

  const modalLayerNumber = openedModalsIds.indexOf(modalId);
  const modalFrontMostLayerNumber = openedModalsIds.length - 1;
  const isFrontMostModal = modalLayerNumber === modalFrontMostLayerNumber;
  const isBackMostModal = modalLayerNumber === 0;
  const isCoveredByOtherModals = !isFrontMostModal;

  React.useEffect(() => {
    if (showed && !openedModalsIds.includes(modalId)) {
      addOpenedModalsIdToRegistry(modalId);
    }
    if (!showed) removeOpenedModalsIdFromRegistry(modalId);
    return () => {
      removeOpenedModalsIdFromRegistry(modalId);
    };
  }, [showed, modalId]);

  React.useEffect(() => {
    if (showed && onOpen) onOpen();
  }, [showed]);

  const { setUnloadBlocker, dropUnloadBlocker } = useUnloadBlocker();

  React.useEffect(() => {
    if (confirmCloseQuestion && showed) setUnloadBlocker();
    else dropUnloadBlocker();
  }, [confirmCloseQuestion, showed, setUnloadBlocker, dropUnloadBlocker]);

  const openModalQuestion = useModalQuestion();

  const closeModal = React.useCallback(() => {
    if (confirmCloseQuestion) {
      openModalQuestion({
        title: "You have unsaved changes.",
        message:
          typeof confirmCloseQuestion === "string"
            ? confirmCloseQuestion
            : "Are you sure you want to close this window?",
        variants: [
          {
            slug: "cancel",
            title: "Cancel",
          },
          {
            slug: "discard",
            title: "Discard",
            variant: "submit",
            action: async () => {
              if (onClose) onClose();
              setShowedState(false);
            },
          },
        ],
      });
    } else {
      if (onClose) onClose();
      setShowedState(false);
    }
  }, [onClose]);

  React.useImperativeHandle(
    ref,
    () => ({
      show: () => {
        setShowedState(true);
      },
      hide: () => {
        closeModal();
      },
    }),
    [closeModal],
  );

  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        // Prevent escape outside fullscreen on macOS
        event.preventDefault();
        event.stopPropagation();

        closeModal();
      }
    };

    if (isFrontMostModal && !dontCloseOnEscapePress) {
      document.addEventListener("keydown", handleKeyDown);
    } else {
      document.removeEventListener("keydown", handleKeyDown);
    }
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [closeModal, modalId, openedModalsIds, dontCloseOnEscapePress, isFrontMostModal]);

  const handleClickOnRoot = React.useCallback(
    (event: React.MouseEvent) => {
      if (dontCloseOnClickOutside) return;
      if (event.target !== event.currentTarget) return;
      if (showed) closeModal();
    },
    [dontCloseOnClickOutside, closeModal, showed],
  );

  const [isBackMostModalDelayed, setIsBackMostModalDelayed] =
    React.useState<boolean>(isBackMostModal);
  React.useEffect(() => {
    if (isBackMostModal) setIsBackMostModalDelayed(true);
  }, [isBackMostModal]);

  return (
    <CSSTransition
      nodeRef={nodeRef0}
      in={showed}
      appear
      timeout={150}
      classNames={CSSTransitionClasses}
      mountOnEnter
      unmountOnExit
      onExited={() => {
        afterClose?.();
        setIsBackMostModalDelayed(false);
      }}
    >
      <div
        className={styles.root}
        data-test-modal-index={modalLayerNumber}
        ref={nodeRef0}
      >
        {(isBackMostModal || isBackMostModalDelayed) && (
          <div className={styles.backmostBackShadow} />
        )}
        <div
          style={
            {
              "--this-modal-z-index": (() => {
                return 1000 + modalLayerNumber;
              })(),
              "--this-modal-width": (() => {
                if (String(width).endsWith("px")) return width;
                if (String(width).endsWith("%")) return width;
                if (String(Number(width)) === String(width)) return String(width) + "px";
              })(),
            } as React.CSSProperties
          }
          className={cx(styles.rootBox, {
            [styles.showed]: showed,
            [styles.hidden]: !showed,
            [styles.coveredByOtherModals]: isCoveredByOtherModals,
            [styles.fullCover]: !!fullCover,
          })}
          data-modal-id={modalId}
        >
          <div
            className={styles.rootContainer}
            onMouseDown={handleClickOnRoot}
            role="button"
            tabIndex={-1}
          >
            <div className={styles.modalVerticalSpacer}></div>
            <div className={styles.modalBox}>
              <div className={styles.modalBoxOverlay} />
              <ModalInnerMemoized
                {...{
                  showed,
                  noHeader,
                  titleAlignLeft,
                  titleWidth100,
                  title,
                  noCloseButton,
                  closeModal,
                  children,
                }}
              />
            </div>
            <div className={styles.modalVerticalSpacer}></div>
          </div>
        </div>
      </div>
    </CSSTransition>
  );
});

PartModal.displayName = "PartModal";

interface ModalInnerMemoizedProps extends PartModalProps {
  closeModal: () => void;
}

const ModalInnerMemoized = (props: ModalInnerMemoizedProps) => {
  /**
   * This helper mainly used for memoize UI on close modal transition
   */
  const lastProps = React.useRef(props);

  if (props.showed) lastProps.current = props;

  const {
    noHeader,
    titleAlignLeft,
    titleWidth100,
    title,
    noCloseButton,
    closeModal,
    children,
  } = lastProps.current;

  return (
    <div className={styles.modalContainer}>
      {not(noHeader) ? (
        <div className={styles.header}>
          <div
            className={cx(styles.headerActionContainer, {
              [styles.titleAlignLeft]: !!titleAlignLeft,
            })}
          ></div>
          <div
            className={cx(styles.title, {
              [styles.titleAlignLeft]: !!titleAlignLeft,
              [styles.titleWidth100]: !!titleWidth100,
            })}
          >
            {title}
          </div>
          <div className={styles.headerActionContainer}>
            {not(noCloseButton) ? (
              <button
                className={styles.headerActionButton}
                onClick={closeModal}
                data-test-element-type="button"
                data-test-action-type="close-modal"
              >
                <CloseIcon />
              </button>
            ) : null}
          </div>
        </div>
      ) : null}
      <div
        className={cx(styles.content, {
          [styles.contentWithoutHeader]: noHeader,
        })}
      >
        {children}
      </div>
    </div>
  );
};

export default PartModal;

export interface ItemsContextValue {
  items: string[];
  addItem: (item: string) => void;
  removeItem: (item: string) => void;
}

const ItemsContext = React.createContext<ItemsContextValue | null>(null);

export function ModalProvider({ children }: { readonly children: React.ReactNode }) {
  const [items, setItems] = React.useState<string[]>([]);

  const addItem = React.useCallback(
    (item: string) => setItems(prev => [...prev.filter(it => it !== item), item]),
    [setItems],
  );

  const removeItem = React.useCallback(
    (item: string) => setItems(prev => prev.filter(x => x !== item)),
    [setItems],
  );

  React.useEffect(() => {
    if (typeof window !== "undefined" && document.querySelector("html")) {
      if (items.length > 0) {
        // @ts-expect-error querySelector is not null here
        document.querySelector("html").dataset.noScrollDueModal = String(items.length);
      } else {
        // @ts-expect-error querySelector is not null here
        delete document.querySelector("html").dataset.noScrollDueModal;
      }
    }
  }, [items.length]);

  const value = React.useMemo(() => ({ items, addItem, removeItem }), [items]);

  return <ItemsContext.Provider value={value}>{children}</ItemsContext.Provider>;
}

export function useModalArray(): ItemsContextValue {
  const ctx = React.useContext(ItemsContext);
  if (!ctx) throw new Error("useModal must be used within ModalProvider");
  return ctx;
}
